/**
 * Cloud-sync UI state: what the cloud currently holds (so cards can show a
 * synced/not-synced badge) and which projects are mid-sync.
 *
 * The cloud list is loaded once (lazily) and refreshed after each sync. When
 * cloud sync isn't configured (no env vars), the store goes dormant and the
 * badge/button render nothing.
 */

import { create } from 'zustand'
import { getBackendClient, type ProjectMeta } from '@/infrastructure/backend'
import { createLogger } from '@/shared/logging/logger'

import { pushProject, pullProject } from '../services/project-sync-service'

const logger = createLogger('CloudSyncStore')

interface CloudSyncState {
  /** False once we learn the backend env vars aren't set. */
  configured: boolean
  /** True after the first (attempted) cloud list load. */
  loaded: boolean
  loading: boolean
  /** Cloud project metadata keyed by id (id → {name, updatedAt}). */
  cloudProjects: Map<string, ProjectMeta>
  /** Ids currently being pushed. */
  syncing: Set<string>

  /** Load the cloud list once; no-op if already loaded/loading. */
  ensureLoaded: () => Promise<void>
  /** Force a re-fetch of the cloud project list. */
  refresh: () => Promise<void>
  /** Push one project (doc + media) and refresh the list afterwards. */
  syncProject: (projectId: string) => Promise<void>
  /**
   * Pull every cloud project that isn't present locally yet, into the workspace.
   * `localIds` are the ids this device already has (so we never clobber local
   * copies). Returns how many were pulled.
   */
  autoPullMissing: (localIds: string[]) => Promise<number>
}

export const useCloudSyncStore = create<CloudSyncState>((set, get) => ({
  configured: true,
  loaded: false,
  loading: false,
  cloudProjects: new Map(),
  syncing: new Set(),

  ensureLoaded: async () => {
    const { loaded, loading } = get()
    if (loaded || loading) return
    await get().refresh()
  },

  refresh: async () => {
    let client: ReturnType<typeof getBackendClient>
    try {
      client = getBackendClient()
    } catch {
      // No backend env configured — go dormant.
      set({ configured: false, loaded: true, loading: false })
      return
    }

    set({ loading: true })
    try {
      const { projects } = await client.listProjects()
      set({
        cloudProjects: new Map(projects.map((p) => [p.id, p])),
        configured: true,
        loaded: true,
        loading: false,
      })
    } catch (error) {
      logger.warn('Failed to load cloud project list', error)
      set({ loading: false, loaded: true })
    }
  },

  syncProject: async (projectId) => {
    let client: ReturnType<typeof getBackendClient>
    try {
      client = getBackendClient()
    } catch {
      set({ configured: false })
      return
    }

    // Mark as syncing (new Set so zustand sees a changed reference).
    set((s) => ({ syncing: new Set(s.syncing).add(projectId) }))
    try {
      await pushProject(projectId, client)
      await get().refresh()
    } catch (error) {
      logger.error(`Failed to sync project ${projectId}`, error)
      throw error
    } finally {
      set((s) => {
        const next = new Set(s.syncing)
        next.delete(projectId)
        return { syncing: next }
      })
    }
  },

  autoPullMissing: async (localIds) => {
    let client: ReturnType<typeof getBackendClient>
    try {
      client = getBackendClient()
    } catch {
      set({ configured: false })
      return 0
    }

    // Make sure we know what the cloud holds, then pull only what's not local.
    await get().refresh()
    const localSet = new Set(localIds)
    const missing = [...get().cloudProjects.keys()].filter((id) => !localSet.has(id))

    let pulled = 0
    for (const id of missing) {
      try {
        await pullProject(id, client)
        pulled += 1
      } catch (error) {
        logger.warn(`Auto-pull failed for project ${id}`, error)
      }
    }
    return pulled
  },
}))
